import React from 'react';
import PropTypes from 'prop-types';

import SeatMap, { ZoneSelector } from '@weezevent/throne';
import Availability from '../../../../models/availability';

import { useTranslation } from 'react-i18next';

import useAppContext from '../../../../context/useAppContext';
import { ToastContext } from '@weezevent/nacre';
import useSeatedContext from '../../../../context/seatedContext';

import { SeatMapBackButton } from '../../../placement/seatmap-back-button';

import Loading from '../../../../components/loading';
import ThroneSeatElements from '../../../placement/throne-seat-elements';

export default function SwapSeatMap({
    zone = null,
    onZoneSelected = () => {},
    availability = null,
    onAvailability = () => {},
    popover = null,
    onSeatOver = () => {},
    onSeatsSelected = () => {},
    onClose = () => {}
}) {
    const { t } = useTranslation();
    let [{ event, organization }] = useAppContext();
    const toast = React.useContext(ToastContext);
    const [{ inventory }] = useSeatedContext();

    const [oldZone, setOldZone] = React.useState(null);
    const [currentZone, setCurrentZone] = React.useState(zone || { uuid: 'root' });
    const [loading, setLoading] = React.useState(true);

    const setZone = React.useCallback(
        zone => {
            onZoneSelected(zone);
            setCurrentZone(zone);
        },
        [onZoneSelected]
    );

    React.useEffect(() => {
        // do not rerender map if same zone
        if (oldZone === currentZone) {
            return;
        }

        setLoading(true);
        setOldZone(currentZone);
        let params = {
            organizationId: organization.id,
            eventId: event.id,
            inventoryId: inventory.uuid
        };
        if (currentZone.uuid !== 'root') {
            params['zoneId'] = currentZone.uuid;
        }
        Availability.getAll(params, {
            disable_social_distancing: 'true'
        })
            .then(availability => {
                onAvailability(availability);
            })
            .catch(e => {
                console.error(e);
                toast.error(t('ticket.swap.availability.retrieval_error'));
                onClose();
            })
            .finally(() => {
                setLoading(false);
            });
    }, [currentZone, oldZone, onAvailability, onClose]);

    if (loading || !availability) {
        return <Loading />;
    }

    if (!availability?.seats?.length) {
        return (
            <div className="swap-seatmap">
                <SeatMapBackButton name={availability.name || ''} onBack={() => setZone({ uuid: availability.parent })} />
                <ZoneSelector
                    zone={availability}
                    onZoneSelected={setZone}
                    showAvailability={false}
                    unavailableColor={false}
                    defaultColor="#0033fa"
                    onBackButtonClicked={() => setZone({ uuid: availability.parent })}
                />
            </div>
        );
    }

    availability.seats.forEach(seat => {
        seat.state = seat.booking_state.toLowerCase();
    });

    return (
        <React.Fragment>
            <div className="swap-seatmap">
                <SeatMapBackButton name={availability.name || ''} onBack={() => setZone({ uuid: availability.parent })} />
                <SeatMap
                    key={availability.key}
                    minimap={false}
                    showScene={false}
                    showPopup
                    zone={availability}
                    onSeatOver={onSeatOver}
                    onSeatClicked={seat => onSeatsSelected([seat])}
                    onSeatsSelected={onSeatsSelected}
                    mapOptions={{
                        showRowNumbers: true,
                        zoomControls: false,
                        offset: false,
                        seatElements: ThroneSeatElements
                    }}
                    popover={popover}
                />
            </div>
        </React.Fragment>
    );
}

SwapSeatMap.propTypes = {
    zone: PropTypes.object,
    onSeatsSelected: PropTypes.func,
    onZoneSelected: PropTypes.func,
    onClose: PropTypes.func,
    availability: PropTypes.object,
    onAvailability: PropTypes.func,
    popover: PropTypes.node,
    onSeatOver: PropTypes.func
};
